import {
  InteractionType,
  MeasureDataType,
  MeasureFormatter,
  MeasureIcon,
} from '@principle-theorem/principle-core/interfaces';
import {
  BigQueryTable,
  IPatientInteractionDimension,
  MeasureTransformMap,
} from '@principle-theorem/reporting/interfaces';
import { QueryOptionResolver } from '../../query-option-resolvers';
import { BaseDimensionMeasures } from '../base-measures';
import { MeasurePath } from '../data-accessor-factory';
import {
  CanBeChartedProperty,
  CanDoAllProperty,
  CanQueryByTimestampProperty,
} from '../measure-properties';
import { MeasurePropertyFactory } from '../measure-property-factory';
import { PatientDimensionMeasures } from './patient-dimension';

export class PatientInteractionDimensionMeasures
  extends BaseDimensionMeasures
  implements
    MeasureTransformMap<
      Pick<IPatientInteractionDimension, 'type' | 'pinned' | 'tags' | 'owner'>
    >
{
  get patient(): PatientDimensionMeasures {
    return new PatientDimensionMeasures(BigQueryTable.Patients, 'patient', {
      sourceJoinKey: MeasurePath.docRef('patient.ref'),
      orderByProperty: MeasurePath.timestamp('updatedAt'),
    });
  }

  get ref(): CanBeChartedProperty {
    const docRef = this.measureRef(MeasurePath.docRef('ref'));
    return MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix('ref'),
        label: 'Patient Interaction Ref',
        dataType: MeasureDataType.String,
        isFilterable: false,
      },
      docRef,
      this.buildQuery().attributes([docRef.attributePath]).get()
    );
  }

  get createdAt(): CanQueryByTimestampProperty {
    const measure = this.measureRef(MeasurePath.timestamp('createdAt'));
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix('createdAt'),
        label: 'Interaction Created Date',
        summary:
          'Date that the patient interaction was created. This is useful for grouping patient interactions by day.',
        formatter: MeasureFormatter.Day,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get title(): CanBeChartedProperty {
    const propertyName = 'title';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.string(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Title',
        summary: 'The title of the patient interaction',
        formatter: MeasureFormatter.Text,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get content(): CanBeChartedProperty {
    const propertyName = 'content';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.string(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Content',
        summary: 'The content of the patient interaction',
        formatter: MeasureFormatter.Text,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get type(): CanDoAllProperty {
    const propertyName = 'type';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.enum<InteractionType>(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Type',
        summary: 'The type of patient interaction',
        optionResolver: QueryOptionResolver.values([
          InteractionType.Call,
          InteractionType.Email,
          InteractionType.Sms,
          InteractionType.Note,
        ]),
        icon: MeasureIcon.List,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      'None'
    );
  }

  get pinned(): CanDoAllProperty {
    const propertyName = 'pinned';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.boolean(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Is Pinned',
        summary: 'Whether the patient interaction is pinned or not',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get tags(): CanDoAllProperty {
    const measure = this.measureRef('tags');
    return MeasurePropertyFactory.arrayRef(
      {
        id: this._pathWithPrefix('tags'),
        label: 'Patient Interaction Tags',
        summary: 'The tags added to the patient interaction',
        dataType: MeasureDataType.String,
        formatter: MeasureFormatter.Text,
        optionResolver: QueryOptionResolver.patientNoteTags(),
        icon: MeasureIcon.Tag,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get owner(): CanDoAllProperty {
    const propertyName = 'owner';
    const name = this.measureRef(`${propertyName}.name`);
    const docRef = this.measureRef(MeasurePath.docRef(`${propertyName}.ref`));
    return MeasurePropertyFactory.label(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Created By',
        summary: 'The user that created the patient interaction',
        optionResolver: QueryOptionResolver.staff(),
        icon: MeasureIcon.Person,
      },
      docRef,
      name,
      this.buildQuery()
        .attributes([docRef.attributePath, name.attributePath])
        .get(),
      'None'
    );
  }
}
